import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { STEPS_MAP, VALIDATION_PER_STEP } from './RetirementWorkflow.constants';
import { retirementValidation } from './RetirementWorkflow.validation';
import {
  FormFields,
  Step,
  StepKey,
  UseRetirementWorkflowActions,
  UseRetirementWorkflowData,
} from './RetirementWorkflow.types';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import { FinancialGoal } from '../../AutoAccountWorkflow.types';
import { hasErrorsOnCurrentFields } from '../../utils/hasErrorsOnCurrentFields';
import { useRetirementMutation } from '../../api/useRetirementMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { formatData } from '../../utils/formatData';
import { AutomatedAccountsDTO } from '../../api/useAutomatedAccountsQuery';

interface Params {
  onSuccess(): void;
  data: AutomatedAccountsDTO;
}

interface UseRetirementWorkflow {
  data: UseRetirementWorkflowData;
  actions: UseRetirementWorkflowActions;
}

export const useRetirementWorkflow = ({ onSuccess, data }: Params): UseRetirementWorkflow => {
  const formBag = useForm<FormFields>({
    defaultValues: formatData(data.retirement),
    resolver: yupResolver(retirementValidation),
  });

  const { watch, trigger, formState, handleSubmit } = formBag;

  const watchAccountType = watch('accountType');

  const { mutateAsync: saveRetirementForm, isLoading } = useRetirementMutation();

  const steps = Object.entries(STEPS_MAP).filter(([, step]) =>
    step({ accountType: watchAccountType }),
  );

  const {
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    progress,
    step,
    stepKey,
  } = useStepNavigation<Step, StepKey>(steps);

  const CurrentStep = step({ accountType: watchAccountType });

  const showNextButton = stepKey !== StepKey.AccountType;

  const onSubmit = async formData => {
    const body = {
      financialGoal: FinancialGoal.Retirement,
      ...formData,
    };

    try {
      await saveRetirementForm({ body });
      onSuccess();
    } catch (err) {
      toast.error('There was an error submitting the form. Please try again later.');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const validateCurrentStep = async () => {
    const currentStepValidationFields = VALIDATION_PER_STEP[stepKey];

    await trigger(currentStepValidationFields);

    const { errors } = formState;

    return hasErrorsOnCurrentFields(errors, currentStepValidationFields);
  };

  return {
    data: {
      CurrentStep,
      isFirstStep,
      isLastStep,
      progress,
      isLoading,
      showNextButton,
    },
    actions: {
      previousStep,
      nextStep,
      onSubmit: handleSubmit(onSubmit),
      formBag,
      validateCurrentStep,
    },
  };
};
